<template lang="pug">
  .SearchInterface
    .row
      .col-12(v-if="mobileLayout")
        btn.rounded-ghost(icon="sliders-h1", @click.native="filtersOpen = true")
          | {{$pgettext('search interface', 'Show filters')}}
        mounting-portal(mount-to="#dialog-root", append)
          transition(name="slide-fade")
            .filter-modal.bg-color-white(v-if="filtersOpen")
              .bottom-bar.bg-color-white
                .container
                  btn.blue-lochmara.block(@click.native="filtersOpen = false")
                    | {{$pgettext('search interface', 'Apply')}}
              .container.filter-modal-header
                .row.justify-content-between
                  .col-4.text-align-left
                    icon.text-30.close-icon(name="times" role="button" @click.native="filtersOpen = false")
                  .col-4.text-align-center
                    p.text-18 {{$pgettext('search interface', 'Filter By')}}
                  .col-4.text-align-right
                    a.text-18.text-color-blue-lochmara.text-20(href="#", @click.prevent="resetFilters") {{$gettext('Clear all')}}
              .container
                .row
                  .filter-container.col-12
                    include ./templates/search-interface/search-filters.pug

      .col-12.col-lg-3.filter-container(v-if="!mobileLayout")
        include ./templates/search-interface/search-filters.pug

      .col-12.col-lg-9.results-col
        .search-bar-wrapper(v-if="!mobileLayout")
          search-bar(ref="search-bar", class="search-input", v-stream:input="textQuery$")
        .results
          slot-wrapper.result-row.row(v-if="!asyncMode", wrapper-class="result-col col-sm-6 col-md-4")
            slot(name="results")
          transition-group.result-row.row(v-if="asyncMode && searchResults", name="staggered-fade", tag="div", :style="{ '--total': searchResults.results.length+1 }")
            .result-col.col-sm-6.col-md-4(v-for="(item, index) in searchResults.results", :key="`${item.date}-${item.main_category}-${item.id}-${index}-${newIndex}`", :data-index="index + 1", :style="{'--i': index+1}")
              div.mt-0.pt-0.mt-lg-3.pt-lg-4.pb-3.pb-lg-0(v-if="entity === 'press-release'")
                excerpt-card.press-room-releases(
                  :link="item.url ? item.url : null",
                  :category-link="item.category_url ? item.category_url : null",
                  :image="item.image && item.image.thumbs ? {src: item.image.thumbs.news_small, alt: item.title} : {src: null, alt: item.title}"
                  :link-text="dataLanguage === 'it' ? 'Leggi' : 'Read'",
                )
                  template(slot="date")
                    p.date {{item.date}}
                  template(slot="category") {{item.main_category}}
                  template(slot="title")
                    h4.title.title-20.text-line-height-medium(v-line-clamp:27="3") {{item.title}}
                  template(slot="excerpt")
                    .excerpt
                      p(v-line-clamp:27="3") {{item.excerpt}}

              image-box.story-card.border-box.align-left.flex-grow-1.d-flex.flex-column.fill-height(
                v-if="entity === 'story'",
                :link="item.url ? item.url : null",
                :category-link="item.category_url ? item.category_url : null",
                :image="{src: item.image && item.image.thumbs ? item.image.thumbs.news_small : null, src_desktop: item.list_image && item.list_image.thumbs ? item.list_image.thumbs.news_small : null, src_mobile: item.mobile_image && item.mobile_image.thumbs ? item.mobile_image.thumbs.news_small : null, alt: item.title}"
                text-container-class="flex-grow-1 d-flex flex-column"
              )
                template(
                  v-if="item.main_category"
                  slot="category"
                ) {{item.main_category}}
                template(slot="title")
                  h4.title-20.text-color-blue.serif
                    span(
                      v-if="$viewportWidth < 992"
                      v-line-clamp:27="3"
                    ) {{item.title}}
                    span(
                      v-else
                      v-line-clamp:27="5"
                    ) {{item.title}}

              div.pb-3(v-if="entity === 'video'")
                video-box.search-result.mb-0.pb-0.mb-lg-3.pb-lg-4(
                  :youtube-id="item.youtube_code",
                  :share-from-preview="true"
                )

              picture-preview-box.search-result.pb-3.pb-lg-5(
                class="search-result"
                v-if="entity === 'photo'"
                :image="{photoUrl: item.image_photo.file, webUrl: item.image_web.file, printUrl: item.image_print.file, alt: item.title}"
              )
                template(slot="title") {{item.title}}
                template(slot="modal-title") {{item.title}}

        pagination.pagination(:async-pages="paginationLinks")
          .links
            slot(name="pagination")
</template>

<script>
  import Vue from 'vue';
  import axios from 'axios';
  import {updateQueryString, getQueryStringParams, renderQueryStringToUrl} from '@helpers/query-string-helpers';
  import {
    delay, pluck, tap, map, catchError,
    debounceTime, distinctUntilChanged,
    mergeMap, skip, finalize, take
  } from 'rxjs/operators';
  import {of, from} from 'rxjs';

  import Accordion from '@components/accordion/accordion';
  import AccordionItem from '@components/accordion/accordion-item';
  import Checkbox from '@components/atoms/checkbox';
  import SearchBox from '@components/search-box';
  import SlotWrapper from '@components/layout/slot-wrapper';
  import ExcerptCard from '@components/excerpt-card';
  import ImageBox from '@components/image-box';
  import VideoBox from '@components/video-box';
  import PicturePreviewBox from '@components/picture-preview-box';
  import Pagination from './pagination';
  import Icon from '@components/atoms/icon';
  import Btn from '@components/atoms/btn';
  import IconLink from '@components/atoms/icon-link';
  import imagesLoaded from "imagesloaded";
  import Velocity from 'velocity-animate';

  export default {
    name: 'SearchInterface',
    components: {
      IconLink,
      Btn,
      Pagination,
      SearchBar: SearchBox,
      Accordion,
      AccordionItem,
      Checkbox,
      SlotWrapper,
      ExcerptCard,
      ImageBox,
      VideoBox,
      PicturePreviewBox,
      Icon
    },
    props: {
      filters: {type: Object},
      entity: {type: String, required: true},
      useFixture: {type: Boolean, default: false},
      apiEndpoint: {type: String, required: true},
      switches: {type: Array | null, default: () => null},
      maxCollapsedFilters: {type: Number, default: 5},
      dataLanguage: {type: String, required: true}
    },
    data() {
      return {
        axios: axios.create({baseURL: this.apiEndpoint}),
        asyncMode: false,
        filterStatus: {},
        filtersExpanded: {},
        filterHeight: 0,
        loading: false,
        filtersOpen: false,
        firstRender:true,
        newIndex: 0 //used to not trigger move transition when elements are reused
      };
    },
    domStreams: ['textQuery$'],
    subscriptions() {
      return {
        // FIXME text query field does not get pre-populated from filter status
        textQuery: this.textQuery$.pipe(
            debounceTime(200),
            pluck('event', 'msg'),
            map(query => query.trim()),
            distinctUntilChanged(),
            tap(query => this.filterStatus.search = query)
        ),
        searchResults: this.$watchAsObservable('filterStatus', {deep: true}).pipe(
            //skip(1),  //removed to force first call for animation
            debounceTime(10),
            pluck('newValue'),
            tap(() => this.loading = true),
            map( (params) => {
              const parsedUrl = getQueryStringParams(window.location.href);
              let p = 1;
              if(parsedUrl.query && parsedUrl.query.page && this.firstRender){
                p = parsedUrl.query.page
              }
              this.firstRender = false;
              return {
                ...params,
                page:p
              }
            }),
            mergeMap(params => this.apiOrFixture(params).pipe(
                tap(() => updateQueryString(params)),
                catchError(error => of({current_page: 1, final_page: 1, results: []})),
                finalize(() => {this.loading = false;this.newIndex++})
            ))
        ),
        setAsyncMode: this.$watchAsObservable('filterStatus', {deep: true}).pipe(
            //skip(1), //removed to force first call for animation
            take(1),
            tap(() => this.asyncMode = true)
        )
      };
    },
    asyncComputed: {
      async filtersOrFixture() {
        return this.filters ? this.filters :
            (await import('@fixtures/filters-fixture')).filters;
      }
    },
    computed: {
      mobileLayout() {
        return this.$viewportWidth < 992;
      },
      paginationLinks() {
        if (this.searchResults) {
          let pageLinks = [];
          let locationParams = getQueryStringParams(location.href);

          for (let i = 0; i < this.searchResults.final_page; i++) {
            const query = {
              ...locationParams.query,
              page: i + 1
            };
            pageLinks.push({
              url: renderQueryStringToUrl(locationParams.url, query),
              label: i + 1,
              active: !locationParams.query.page ? i === 0 : i === locationParams.query.page-1
            });
          }
          return pageLinks;
        } else {
          return false;
        }
      },
      tooManyFilters() {
        return (category, maxAmount = this.maxCollapsedFilters) => {
          return this.filtersOrFixture[category.key].items.length > maxAmount;
        };
      }
    },
    methods: {
      getFilterHeight() {
        // TODO calculate exact block height
        if (this.$refs['filter'] && this.$refs['filter'].length) {
          this.filterHeight = this.$refs['filter'][0].$el ? this.$refs['filter'][0].$el.clientHeight : 0;
        } else {
          this.filterHeight = 0;
        }
      },
      initFilterStatus(filters) {
        this.filterStatus = Object.keys(filters).reduce((acc, category) => {
          const filter = filters[category];

          if (filter.items && filter.items.length > this.maxCollapsedFilters) {
            Vue.set(this.filtersExpanded, filter.key, false);
          } else {
            Vue.set(this.filtersExpanded, filter.key, true);
          }

          switch (filter.type) {
            case 'text':
              acc[category] = filter.value;
              break;
            case 'radio':
            case 'checkbox':
              // TODO handle arrays in case of checkbox mode
              acc[category] = filter.items.filter(f => f.checked).map(f => f.value)[0];
              break;
            default:
              break;
          }
          return acc;
        }, {});

        this.getFilterHeight();
      },
      apiOrFixture(params) {
        if (this.useFixture) {
          return this.entity === 'story' || this.entity === 'press-release' ?
              from(import('@fixtures/stories-fixture')).pipe(
                  pluck('searchResults'),
                  delay(1000)
              ) :
              this.entity === 'video' ?
                  from(import('@fixtures/videos-fixture')).pipe(
                      pluck('searchResults'),
                      delay(1000)
                  ) :
                  this.entity === 'photo' ?
                      from(import('@fixtures/photos-fixture')).pipe(
                          pluck('searchResults'),
                          delay(1000)
                      ) : '';
        } else {
          return from(this.axios.get('', {params})).pipe(
              pluck('data')
          );
        }
      },
      resetFilters() {
        this.filterStatus = Object.keys(this.filterStatus).reduce((acc, k) => {
          acc[k] = null;
          return acc;
        }, {});
        this.$refs['search-bar'].clear();
      },
      beforeEnter(el) {
        //el.style.opacity = 0;
        //el.style.transform = "translateY(0)";
      },
      afterEnter(el){
        //console.log( this.searchResults.results.length,Number(el.dataset.index) + 1 )
      },
      enter(el, done) {
        //imagesLoaded(el, () => {
        //  const delay = el.dataset.index * 100;
        //  Velocity(el, { opacity: 1, translateY: [20, 0] }, {delay, complete: done });
        //});
      },
      leave(el, done) {
        //const delay = 100;
        //Velocity(el, {opacity: 0}, {delay, complete: done});
      }
    },
    watch: {
      filtersOpen(val) {
        val && setTimeout(() => this.getFilterHeight(), 10);
      }
    },
    mounted() {
      if (this.filtersOrFixture) {
        this.initFilterStatus(this.filtersOrFixture);
      } else {
        this.$watch('filtersOrFixture', filters => this.initFilterStatus(filters));
      }

      const parsedUrl = getQueryStringParams(window.location.href);
      if (this.$refs['search-bar']) {
        this.$refs['search-bar'].$refs.input.value = (parsedUrl.query && parsedUrl.query.search) || '';
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import "~$scss/variables";
  @import "~$scss/utility";

  .filter-modal {
    position: fixed;
    z-index: 10;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    overflow: scroll;
    padding-bottom: 60px;

    .bottom-bar {
      position: fixed;
      z-index: 11;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 10px 0 20px;
      box-shadow: 0 0 6px fade-out($color-black, 0.84);
    }

    .filter-modal-header {
      border-bottom: 1px solid fade-out($color-grey, 0.75);
      margin-bottom: 25px;

      .row {
        min-height: 60px;
      }

      [class*="col-"] {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }

    .filter-container {
      .search-input {
        margin-bottom: 50px;
      }
    }
  }

  .filter-container {
    @media (min-width: $bootstrap-md) {
      padding-right: 50px;
    }

    .clear {
      margin-bottom: 24px;
    }

    .switches {
      display: flex;
      margin-bottom: 50px;
      border: 1px solid fade-out($color-grey, 0.75);
      border-radius: 4px;
      padding: 3px;

      .switch {
        flex: 0 0 50%;
        max-width: 50%;
        padding: 10px 0;
        border-radius: 4px;
        text-align: center;
        color: $color-blue-lochmara;
        font-size: 20px;

        &.active {
          background-color: $color-blue-lochmara;
          color: $color-white;
        }
      }
    }

    .filter-category {
      margin-bottom: 35px;

      &:last-child {
        margin: 0;
      }
    }

    .category-header {
      margin-bottom: 23px;
      padding-bottom: 11px;
      border-bottom: 1px solid fade-out($color-grey, 0.75);
      text-transform: uppercase;
      color: $color-grey;
      letter-spacing: 0.1em;
      font-size: 14px;
      font-weight: $extra-bold;
      line-height: 1.57em;
    }

    .filters {
      overflow: hidden;
      transition: max-height .5s ease-out;

      .filter {
        display: flex;
        align-items: center;
        padding-bottom: 20px;

        @media (max-width: $bootstrap-md - 1px) {
          flex-direction: row-reverse;
          justify-content: space-between;
        }

        ::v-deep .box {
          margin-bottom: 1px;

          @media (max-width: $bootstrap-md - 1px) {
            margin-right: 0;
          }
        }
      }
    }

    .expand-filters-wrapper {
      padding-bottom: 24px;

      .expand-filters {
        font-size: 20px;

        .chevron {
          display: inline-block;
          vertical-align: middle;
          margin-left: 10px;
          font-size: 32px;
          transform: rotate(0);
          transition: transform .5s ease-out;
        }

        &.expanded .chevron {
          transform: rotate(180deg);
        }
      }
    }
  }

  .result-row {
    @include responsive-property(padding-top, $section-padding);
    margin-bottom: -40px;
  }

  .pagination {
    text-align: center;
    border-top: 1px solid fade-out($color-grey, 0.75);
    margin-top: 60px;
    padding-top: 30px;

    @media (min-width: $bootstrap-lg) {
      margin-top: 135px;
    }

    .page, .page:visited {
      display: inline-block;
      font-size: 20px;
      margin-right: 35px;
      color: fade-out($color-dark, 0.5);
      line-height: 1.4em;

      &:last-child {
        margin-right: 0;
      }

      &.active {
        color: $color-blue-lochmara;
        border-bottom: 1px solid $color-blue-lochmara;
      }
    }
  }
</style>

<style lang="scss">
  @import "~$scss/variables";

  .SearchInterface {
    .result-col {
      padding-bottom: $grid-gutter-width;
    }

    &.stories-lists{

      .rounded-ghost{
        margin-bottom:30px;
      }
     .result-row{
        @media(min-width:$bootstrap-lg){
        padding-top:60px;
      }
     }
      .result-col{
        padding-bottom:30px;

         @media(min-width:$bootstrap-lg){
         padding-bottom:24px;
      }
      }
     }

    &.press-room-lists{

      .rounded-ghost{
        margin-bottom:32px;
      }
    }
  }
  .fade{
    &-enter-active{
      transition: opacity .5s linear, transform .5s cubic-bezier(.2,.5,.1,1);
    }
    &-leave-active {
      transition: opacity .4s linear, transform .4s cubic-bezier(.5,0,.7,.4);
    }

    &-enter, &-leave-to {
      opacity: 0;
    }
    &-enter { transform: translateY(-1em); }
    &-leave-to { transform: translateY(1em); }
  }
  .staggered-fade {

    //staggered removed because when element disappear other elements take places so
    //it seems like everything was moving and popping out
    //really bad effect

    &-move {
      transition: opacity .5s linear, transform .5s ease-in-out;
    }

    &-leave-active {
      transition: opacity .4s linear, transform .4s cubic-bezier(.5,0,.7,.4); //cubic-bezier(.7,0,.7,1);
      //transition-delay: calc( 0.1s * (var(--total) - var(--i)) );
    }

    &-enter-active {
      transition: opacity .5s linear, transform .5s cubic-bezier(.2,.5,.1,1);
      transition-delay: .5s;//calc( 0.1s * var(--i) );
    }

    &-enter,
    &-leave-to {
      opacity: 0;
    }

    &-enter { transform: translateY(-1em); }
    &-leave-to { transform: translateY(1em); }

  }
</style>
